<template>
  <div class="home">
    <section class="hero" aria-labelledby="hero-title">
      <div class="container hero-grid">
        <div class="hero-text">
          <h1 id="hero-title">Devis, factures et facturation électronique pour les TPE et indépendants</h1>
          <p class="lead">
            Une seule application web réunit vos prospects (CRM), devis, acomptes, factures Factur-X, encaissements, achats, stock et export comptable FEC : un ERP léger pour TPE et indépendants.
            Développé à Toulouse par Franck Besson, fondateur de Citron Digital.
          </p>
          <div class="btn-row">
            <a class="btn btn--primary" :href="site.productUrl" rel="noopener">Accéder au gestionnaire</a>
            <router-link class="btn btn--ghost" to="/gestionnaire-citron">Voir les fonctionnalités</router-link>
          </div>
        </div>
        <document-demo/>
      </div>
    </section>

    <section class="band band--ink reform" aria-labelledby="reform-title">
      <div class="container reform-grid">
        <div>
          <h2 id="reform-title">La facture électronique devient la règle en 2027</h2>
          <p class="muted">
            Les factures entre entreprises assujetties à la TVA passent par une plateforme agréée, au lieu d'un PDF envoyé par e-mail.
            Le logiciel prépare vos factures au bon format.
          </p>
          <p class="links">
            <router-link to="/blog/facturation-electronique-2026-2027-calendrier-obligations">Lire le calendrier et les obligations</router-link>
            <router-link to="/gestionnaire-citron/facturation-electronique">Voir ce que fait le logiciel</router-link>
          </p>
        </div>
        <dl class="dates">
          <div>
            <dt>Depuis le 1<sup>er</sup> septembre 2026</dt>
            <dd>Toutes les entreprises assujetties à la TVA doivent pouvoir <strong>recevoir</strong> des factures électroniques.</dd>
          </div>
          <div>
            <dt>À partir du 1<sup>er</sup> septembre 2027</dt>
            <dd>Les TPE, PME et micro-entreprises doivent <strong>émettre</strong> leurs factures sous forme électronique.</dd>
          </div>
        </dl>
      </div>
    </section>

    <section class="band" aria-labelledby="features-title">
      <div class="container">
        <h2 id="features-title">Un dossier suivi du premier contact à la comptabilité</h2>
        <p class="lead">Chaque information est saisie une fois : la fiche client alimente le devis, l'acompte se déduit de la facture finale, les achats se rattachent au dossier et tout alimente l'export comptable et les statistiques.</p>
        <div class="features-grid">
          <ul class="feature-list">
            <li v-for="feature in features" :key="feature.slug">
              <h3><router-link :to="feature.path">{{ feature.name }}</router-link></h3>
              <p>{{ feature.summary }}</p>
            </li>
          </ul>
          <figure class="shot">
            <img src="/img/screen_app/dossier-achats.webp" width="1528" height="625" loading="lazy"
                 alt="Vue d'un dossier du logiciel de facturation : montants vendu, acheté, marge et reste à payer, contrats avec devis, acompte et facture, achats rattachés">
            <figcaption class="muted">Un dossier : vendu, acheté, marge et reste à payer, avec ses devis, factures et achats.</figcaption>
          </figure>
        </div>
      </div>
    </section>

    <section class="band band--white" aria-labelledby="guides-title">
      <div class="container">
        <div class="section-head">
          <h2 id="guides-title">Guides pratiques sur la facturation</h2>
          <router-link to="/blog">Tous les guides</router-link>
        </div>
        <article-list :articles="latest"/>
      </div>
    </section>

    <section class="band" aria-labelledby="services-title">
      <div class="container">
        <h2 id="services-title">Un développeur à Toulouse pour votre site ou votre logiciel sur mesure</h2>
        <p class="lead">Depuis 2020, Franck Besson conçoit des sites et des applications web, et reprend des logiciels existants.</p>
        <div class="services-grid">
          <div>
            <h3><router-link to="/site-web">Création de site vitrine</router-link></h3>
            <p>Un site sur mesure, pensé pour le référencement naturel, avec hébergement, nom de domaine et maintenance.</p>
          </div>
          <div>
            <h3><router-link to="/logiciel">Logiciel et application web sur mesure</router-link></h3>
            <p>Création, correction de bugs, évolution, migration et sécurisation de logiciels et d'applications SaaS.</p>
          </div>
          <div>
            <h3><router-link to="/portfolio">À propos</router-link></h3>
            <p>Mon parcours de développeur full stack, mes expériences et mes formations.</p>
          </div>
        </div>
      </div>
    </section>

    <advices-component/>

    <section class="band" aria-labelledby="faq-title">
      <div class="container container--narrow">
        <h2 id="faq-title">Questions fréquentes</h2>
        <faq-section :items="faq"/>
      </div>
    </section>

    <call-to-action-component/>
  </div>
</template>

<script>
  import site from "@/content/site";
  import features from "@/content/features";
  import articles from "@/content/articles";
  import {useSeo} from "@/seo/useSeo";
  import {organization, person, website, software, webPage, faqPage} from "@/seo/schema";
  import pages from "@/content/pages";
  import DocumentDemo from "@/components/DocumentDemo.vue";
  import ArticleList from "@/components/ArticleList.vue";
  import FaqSection from "@/components/FaqSection.vue";
  import AdvicesComponent from "@/components/AdvicesComponent.vue";
  import CallToActionComponent from "@/components/CallToActionComponent.vue";

  const FAQ = [
    {
      q: 'À qui s\'adresse ce logiciel de gestion et de facturation ?',
      a: 'Aux TPE, aux indépendants et aux petites équipes qui veulent suivre leurs prospects, leurs devis, leurs factures et leurs encaissements au même endroit, sans tableur.'
    },
    {
      q: 'Le logiciel produit-il des factures électroniques Factur-X ?',
      a: 'Oui. Les factures, factures d\'acompte et avoirs peuvent être produits au format Factur-X et transmis par une plateforme agréée partenaire. Le détail est sur la page [facturation électronique](/gestionnaire-citron/facturation-electronique).'
    },
    {
      q: 'Quand la facture électronique devient-elle obligatoire ?',
      a: 'Depuis le 1er septembre 2026, toutes les entreprises assujetties à la TVA établies en France doivent pouvoir recevoir des factures électroniques. L\'obligation d\'en émettre arrive le 1er septembre 2027 pour les TPE, PME et micro-entreprises. Voir le [calendrier complet](/blog/facturation-electronique-2026-2027-calendrier-obligations).'
    },
    {
      q: 'Le logiciel gère-t-il les achats, le stock et l\'export comptable (FEC) ?',
      a: 'Oui. Vous enregistrez vos factures fournisseurs, vos paiements et votre stock, puis vous exportez les écritures au format FEC ou dans un classeur Excel pour votre comptable. Voir [achats et logistique](/gestionnaire-citron/achats-logistique) et [export comptable](/gestionnaire-citron/export-comptable).'
    },
    {
      q: 'Peut-on travailler à plusieurs et donner accès à son comptable ?',
      a: 'Oui. L\'espace entreprise est partagé, chaque utilisateur a des droits selon son rôle, et un accès comptable limité aux données utiles peut être donné à votre expert-comptable, avec un export comptable (FEC et Excel).'
    },
    {
      q: 'Comment établir une facture d\'acompte puis la facture finale ?',
      a: 'Depuis le devis accepté, vous générez la facture d\'acompte ; à la fin de la prestation, la facture finale (ou définitive) reprend le total et déduit l\'acompte déjà facturé. Voir le guide [facture d\'acompte et facture finale](/blog/facture-acompte-facture-finale) et la fonction [devis et factures](/gestionnaire-citron/devis-factures).'
    },
    {
      q: 'Faut-il un ERP ou un CRM pour une TPE ?',
      a: 'Pas forcément. Un logiciel de gestion qui relie prospects, devis, factures, achats et comptabilité couvre souvent le besoin d\'une TPE sans la lourdeur d\'un ERP. Le guide [ERP, CRM ou logiciel de facturation](/blog/erp-crm-logiciel-facturation-tpe) aide à choisir.'
    },
    {
      q: 'Citron Digital réalise-t-il aussi des sites et des logiciels sur mesure ?',
      a: 'Oui. Franck Besson crée des [sites vitrines](/site-web) et des [applications et logiciels sur mesure](/logiciel) depuis 2020, à Toulouse et pour des clients partout en France.'
    }
  ];

  export default {
    name: 'HomeView',
    components: {DocumentDemo, ArticleList, FaqSection, AdvicesComponent, CallToActionComponent},
    setup() {
      const page = pages.find(entry => entry.path === '/');
      useSeo({
        path: '/',
        graph: [
          organization(),
          person(),
          website(),
          software(),
          webPage('/', page.title, page.description, {about: {'@id': software()['@id']}}),
          faqPage(FAQ)
        ]
      });
    },
    data: function () {
      return {
        site,
        features,
        faq: FAQ,
        latest: ['facture-acompte-facture-finale', 'facture-avoir-comment-etablir', 'fec-fichier-des-ecritures-comptables', 'export-comptable-fec-excel-expert-comptable']
            .map(slug => articles.find(article => article.slug === slug)),
      }
    }
  }
</script>

<style lang="stylus" scoped>
@import '~/src/common/style/constants.styl';

// ---------------------------------------------------------- accueil
.hero
  padding-top clamp(28px, 6vw, 72px)
  padding-bottom clamp(48px, 8vw, 96px)
  overflow hidden

.hero-grid
  display grid
  gap 48px
  align-items start

.hero-text
  padding-top clamp(0px, 4vw, 48px)
  h1
    max-width 18ch
  .lead
    margin-top 22px
  .btn-row
    margin-top 30px

// ---------------------------------------------------------- la réforme
.reform-grid
  display grid
  gap 40px
  align-items start
  h2
    max-width 20ch
  .muted
    margin 18px 0 0
    max-width 52ch
  .links
    display flex
    flex-direction column
    align-items flex-start
    gap 8px
    margin-top 22px

.dates
  margin 0
  border-top 2px solid LEMON
  > div
    padding 20px 0
    border-bottom 1px solid rgba(255, 255, 255, 0.25)
  dt
    font-family 'Calder-LC', 'Trebuchet MS', system-ui, sans-serif
    font-size 1.5rem
    line-height 1.2
    color LEMON
  dd
    margin 8px 0 0
    max-width 46ch
    color #E3E8F6

// ---------------------------------------------------------- fonctions
.features-grid
  display grid
  gap 40px
  margin-top 40px
  align-items start

.feature-list
  list-style none
  margin 0
  padding 0
  border-top 2px solid INK
  li
    margin 0
    padding 20px 0
    border-bottom 1px solid RULE
  h3
    margin 0 0 6px
    font-size 1.4rem
    a
      text-decoration none
      &:hover
        text-decoration underline
  p
    margin 0
    color INK_SOFT
    max-width 52ch

.shot
  margin 0
  img
    width 100%
    height auto
    border 2px solid INK
    border-radius 4px
    background WHITE
  figcaption
    margin-top 10px
    font-size 0.9rem

// ---------------------------------------------------------- guides, services
.section-head
  display flex
  flex-wrap wrap
  align-items baseline
  justify-content space-between
  gap 8px 24px
  margin-bottom 28px
  h2
    margin 0

.services-grid
  display grid
  gap 32px
  margin-top 36px
  h3
    margin 0 0 8px
    font-size 1.35rem
  p
    margin 0
    color INK_SOFT
    max-width 40ch
  > div
    padding-top 16px
    border-top 2px solid INK

@media (min-width: DESKTOP_SIZE)
  .hero-grid
    grid-template-columns minmax(0, 1fr) minmax(0, 480px)
    gap 56px
  .reform-grid
    grid-template-columns 1fr 1fr
    gap 72px
  .features-grid
    grid-template-columns 1fr 1.15fr
    gap 56px
  .services-grid
    grid-template-columns repeat(3, 1fr)
</style>
